import { useStore } from "./store";

/** Short sounds synthesized with WebAudio, no asset files. */
export type SoundName =
  | "dice"
  | "doubles"
  | "cash"
  | "jail"
  | "card"
  | "build"
  | "turn"
  | "buy"
  | "auction"
  | "bid"
  | "trade"
  | "bankrupt"
  | "win";

interface Bus {
  ctx: AudioContext;
  out: GainNode;
  noise: AudioBuffer;
}

let bus: Bus | null = null;
let unlocked = false;
let listening = false;

/** Browsers only allow audio after a user gesture, so nothing plays before the first one. */
function listenForGesture() {
  if (listening || typeof window === "undefined") return;
  listening = true;
  const unlock = () => {
    unlocked = true;
    window.removeEventListener("pointerdown", unlock, true);
    window.removeEventListener("keydown", unlock, true);
    window.removeEventListener("touchend", unlock, true);
  };
  window.addEventListener("pointerdown", unlock, true);
  window.addEventListener("keydown", unlock, true);
  window.addEventListener("touchend", unlock, true);
}
listenForGesture();

function getBus(): Bus | null {
  if (bus) return bus;
  const Ctor: typeof AudioContext | undefined =
    typeof window === "undefined" ? undefined : (window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
  if (!Ctor) return null;
  try {
    const ctx = new Ctor();
    const out = ctx.createGain();
    out.gain.value = 0.5;
    const compressor = ctx.createDynamicsCompressor();
    out.connect(compressor).connect(ctx.destination);
    const noise = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const data = noise.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    bus = { ctx, out, noise };
    return bus;
  } catch {
    return null;
  }
}

interface ToneOptions {
  freq: number;
  /** Frequency at the end of the tone (glide). */
  to?: number;
  dur: number;
  type?: OscillatorType;
  gain?: number;
  /** Seconds after the start of the sound. */
  at?: number;
  attack?: number;
}

function tone(b: Bus, t0: number, o: ToneOptions) {
  const { ctx } = b;
  const start = t0 + (o.at ?? 0);
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = o.type ?? "sine";
  osc.frequency.setValueAtTime(o.freq, start);
  if (o.to) osc.frequency.exponentialRampToValueAtTime(o.to, start + o.dur);
  const peak = o.gain ?? 0.2;
  const attack = o.attack ?? 0.006;
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(peak, start + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, start + o.dur);
  osc.connect(g).connect(b.out);
  osc.start(start);
  osc.stop(start + o.dur + 0.05);
}

interface NoiseOptions {
  dur: number;
  freq: number;
  to?: number;
  q?: number;
  filter?: BiquadFilterType;
  gain?: number;
  at?: number;
}

function burst(b: Bus, t0: number, o: NoiseOptions) {
  const { ctx } = b;
  const start = t0 + (o.at ?? 0);
  const src = ctx.createBufferSource();
  src.buffer = b.noise;
  const filter = ctx.createBiquadFilter();
  filter.type = o.filter ?? "bandpass";
  filter.frequency.setValueAtTime(o.freq, start);
  if (o.to) filter.frequency.exponentialRampToValueAtTime(o.to, start + o.dur);
  filter.Q.value = o.q ?? 1;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(o.gain ?? 0.2, start + 0.004);
  g.gain.exponentialRampToValueAtTime(0.0001, start + o.dur);
  src.connect(filter).connect(g).connect(b.out);
  src.start(start, Math.random() * 0.5);
  src.stop(start + o.dur + 0.05);
}

const SOUNDS: Record<SoundName, (b: Bus, t: number) => void> = {
  // Dice rattling on a table: irregular clacks that thin out, then one last knock.
  dice(b, t) {
    const clacks = [0, 0.07, 0.13, 0.22, 0.29, 0.4, 0.5, 0.63, 0.78];
    clacks.forEach((at, i) => {
      const fade = 1 - i / (clacks.length + 2);
      burst(b, t, { at, dur: 0.05, freq: 1500 + Math.random() * 1400, q: 3, gain: 0.32 * fade });
      tone(b, t, { at, freq: 240 + Math.random() * 120, dur: 0.04, type: "triangle", gain: 0.06 * fade });
    });
  },
  // The same rattle, then a bright sparkle for the extra roll.
  doubles(b, t) {
    SOUNDS.dice(b, t);
    [1318.5, 1760, 2637].forEach((f, i) => tone(b, t, { at: 0.86 + i * 0.07, freq: f, dur: 0.35, type: "sine", gain: 0.08 }));
  },
  // Coins: two bright metallic pings.
  cash(b, t) {
    for (const [at, f] of [[0, 1760], [0.09, 2349]] as const) {
      tone(b, t, { at, freq: f, dur: 0.45, type: "sine", gain: 0.13 });
      tone(b, t, { at, freq: f * 2.76, dur: 0.18, type: "sine", gain: 0.04 });
    }
    burst(b, t, { dur: 0.05, freq: 6000, filter: "highpass", gain: 0.06 });
  },
  // Cell door: low thud, clang and a short rattle of the lock.
  jail(b, t) {
    tone(b, t, { freq: 110, to: 45, dur: 0.35, type: "sine", gain: 0.4 });
    tone(b, t, { at: 0.02, freq: 310, dur: 0.5, type: "square", gain: 0.05 });
    tone(b, t, { at: 0.02, freq: 467, dur: 0.4, type: "square", gain: 0.035 });
    for (const at of [0.32, 0.4, 0.45]) burst(b, t, { at, dur: 0.04, freq: 3200, q: 4, gain: 0.12 });
  },
  // Card flick: two quick swishes.
  card(b, t) {
    burst(b, t, { dur: 0.13, freq: 1200, to: 4200, q: 0.8, gain: 0.16 });
    burst(b, t, { at: 0.11, dur: 0.1, freq: 2000, to: 5200, q: 0.8, gain: 0.1 });
  },
  // Hammer taps on wood.
  build(b, t) {
    for (const [at, f] of [[0, 320], [0.16, 300], [0.36, 360]] as const) {
      tone(b, t, { at, freq: f, to: f * 0.6, dur: 0.09, type: "triangle", gain: 0.3 });
      burst(b, t, { at, dur: 0.03, freq: 1800, q: 2, gain: 0.14 });
    }
  },
  // Soft two-note chime: it is your turn.
  turn(b, t) {
    tone(b, t, { freq: 659.25, dur: 0.5, gain: 0.11, attack: 0.02 });
    tone(b, t, { at: 0.14, freq: 880, dur: 0.7, gain: 0.11, attack: 0.02 });
  },
  // Rising arpeggio with a coin ping on top.
  buy(b, t) {
    [523.25, 659.25, 783.99].forEach((f, i) => tone(b, t, { at: i * 0.08, freq: f, dur: 0.22, type: "triangle", gain: 0.16 }));
    tone(b, t, { at: 0.26, freq: 2093, dur: 0.4, gain: 0.07 });
  },
  // Auctioneer's hammer: a hard knock with a wooden ring.
  auction(b, t) {
    tone(b, t, { freq: 190, to: 90, dur: 0.16, type: "triangle", gain: 0.5 });
    tone(b, t, { freq: 620, dur: 0.22, type: "sine", gain: 0.1 });
    burst(b, t, { dur: 0.05, freq: 1400, q: 1.5, gain: 0.3 });
  },
  // A raised paddle: short wooden tick with a lift.
  bid(b, t) {
    tone(b, t, { freq: 520, to: 780, dur: 0.08, type: "triangle", gain: 0.14 });
    burst(b, t, { dur: 0.025, freq: 2400, q: 2, gain: 0.08 });
  },
  // Doorbell: someone wants to trade.
  trade(b, t) {
    tone(b, t, { freq: 659.25, dur: 0.6, type: "triangle", gain: 0.13, attack: 0.01 });
    tone(b, t, { at: 0.28, freq: 523.25, dur: 0.9, type: "triangle", gain: 0.13, attack: 0.01 });
    tone(b, t, { at: 0.28, freq: 1046.5, dur: 0.5, type: "sine", gain: 0.03 });
  },
  // Sinking minor line.
  bankrupt(b, t) {
    [329.63, 293.66, 246.94, 196].forEach((f, i) =>
      tone(b, t, { at: i * 0.19, freq: f, to: f * 0.97, dur: i === 3 ? 0.8 : 0.3, type: "sawtooth", gain: 0.07 }),
    );
    tone(b, t, { at: 0.57, freq: 98, dur: 0.9, type: "sine", gain: 0.2 });
  },
  // Short fanfare.
  win(b, t) {
    const notes: [number, number, number][] = [
      [523.25, 0, 0.16],
      [659.25, 0.16, 0.16],
      [783.99, 0.32, 0.16],
      [1046.5, 0.48, 0.6],
    ];
    for (const [f, at, dur] of notes) {
      tone(b, t, { at, freq: f, dur, type: "triangle", gain: 0.17 });
      tone(b, t, { at, freq: f * 2, dur, type: "sine", gain: 0.04 });
    }
    tone(b, t, { at: 0.48, freq: 523.25, dur: 0.8, type: "triangle", gain: 0.1 });
  },
};

/** Plays a sound. Does nothing when sound is off, before the first user gesture, or without WebAudio. */
export function playSound(name: SoundName): void {
  if (!useStore.getState().soundOn || !unlocked) return;
  const b = getBus();
  if (!b) return;
  const play = () => {
    try {
      SOUNDS[name](b, b.ctx.currentTime + 0.02);
    } catch {
      /* a failing sound must never break the game */
    }
  };
  if (b.ctx.state === "suspended") {
    b.ctx.resume().then(play, () => undefined);
  } else {
    play();
  }
}
